import PropTypes from "prop-types";
import { smartFetch } from '../foundation/smartfetch';
import { buildUrl } from '../foundation/urlbuilder';
import { decode } from 'html-entities';
import { marked } from 'marked';
import { CacheManager } from '../foundation/cache-manager';
import { getDomain } from '../foundation/utilities';
const wpApiURL = "https://public-api.wordpress.com/rest/v1/sites/";
const wpCategoriesPath = "/categories";
const wpCacheTTL = 1000 * 60 * 60 * 24 * 7; // 1 week
const wpCache = new CacheManager({ mode: 'local', ttl: wpCacheTTL, domain: getDomain(), namespace: 'wp' });
function normalizeMarkdownLinks(markdown) {
    return markdown.replace(/\[((?:https?:\/\/)[^\]\s]+)\](?!\()/gi, '[$1]($1)');
}
export function htmlToWordPressBlocks(html) {
    if (!html.trim() || /<!--\s*wp:/i.test(html))
        return html;
    return html.replace(/<(h[1-6]|p|ul|ol|blockquote|pre)\b[^>]*>[\s\S]*?<\/\1>|<hr\b[^>]*\/?>/gi, (block) => {
        const tag = block.match(/^<(h[1-6]|p|ul|ol|blockquote|pre|hr)\b/i)?.[1]?.toLowerCase();
        if (!tag)
            return block;
        if (tag.startsWith('h')) {
            const level = Number(tag.slice(1));
            return `<!-- wp:heading {"level":${level}} -->${block}<!-- /wp:heading -->`;
        }
        if (tag === 'ul' || tag === 'ol') {
            const attributes = tag === 'ol' ? ' {"ordered":true}' : '';
            return `<!-- wp:list${attributes} -->${block}<!-- /wp:list -->`;
        }
        if (tag === 'blockquote')
            return `<!-- wp:quote -->${block}<!-- /wp:quote -->`;
        if (tag === 'pre')
            return `<!-- wp:code -->${block}<!-- /wp:code -->`;
        if (tag === 'hr')
            return `<!-- wp:separator -->${block}<!-- /wp:separator -->`;
        return `<!-- wp:paragraph -->${block}<!-- /wp:paragraph -->`;
    });
}
/**
 * Converts WordPress REST API blog post to schema.org BlogPosting format
 * @param post WordPress blog post
 * @param includeFullContent Whether to include articleBody (true) or just description (false)
 */
export function mapWordPressToBlogPosting(post, includeFullContent = false, relatedServices = []) {
    const cleanContent = (content) => {
        if (!content)
            return '';
        // Strip HTML tags and decode all HTML entities
        const stripped = content.replace(/<[^>]*>/g, '');
        return decode(stripped).replace(/\[…\]/g, '').trim();
    };
    const description = cleanContent(post.excerpt);
    const articleBody = includeFullContent ? cleanContent(post.content || '') : undefined;
    const schema = {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        url: post.URL,
        headline: decode(post.title.replace(/<[^>]*>/g, '')),
        description: description || decode(post.title.replace(/<[^>]*>/g, '')),
        datePublished: post.date,
        image: post.featured_image || post.post_thumbnail?.URL,
        articleSection: Array.isArray(post.categories) && post.categories.length > 0
            ? post.categories[0]
            : 'Blog',
        keywords: Array.isArray(post.categories) ? post.categories : [],
    };
    if (articleBody) {
        schema.articleBody = articleBody;
    }
    if (post.modified) {
        schema.dateModified = post.modified;
    }
    if (post.author) {
        schema.author = {
            '@type': 'Person',
            name: post.author.name,
            url: `https://${new URL(post.URL).hostname}/author/${post.author.login}`,
        };
    }
    const serviceAbout = relatedServices
        .filter((service) => Boolean(service?.url))
        .map((service) => ({
        '@type': 'WebPage',
        '@id': service.url,
        url: service.url,
        name: service.name,
    }));
    if (serviceAbout.length > 0) {
        schema.about = serviceAbout;
    }
    return schema;
}
/**
 * getWordPressItems — Fetch posts from the WordPress REST API for a given site.
 *
 * @param {string} [props.site] - WordPress site identifier (site slug or domain).
 * @param {number} [props.count] - Optional number of posts to fetch (omit to fetch all available).
 * @param {string} [props.baseURL] - Optional base URL for the WordPress API (defaults to public WordPress API URL).
 */
getWordPressItems.propTypes = {
    /** WordPress site identifier (slug or domain) */
    site: PropTypes.string.isRequired,
    /** Number of posts to fetch (optional) */
    count: PropTypes.number,
    /** Base URL for WordPress API (optional) */
    baseURL: PropTypes.string,
};
export async function getWordPressItems(props) {
    const { baseURL = wpApiURL } = props;
    const requested = props.count; // undefined means fetch all available
    const tag = `wp-posts-${props.site}`; // unique per site so we can invalidate fetch cache separately
    let posts = [];
    let page = 1;
    while (true) {
        const remaining = requested ? Math.max(requested - posts.length, 0) : 100;
        const number = Math.min(remaining || 100, 100);
        const wpPostsURL = buildUrl({
            baseUrl: baseURL,
            pathSegments: [props.site, 'posts'],
            params: { number, page },
        });
        try {
            const data = await smartFetch(wpPostsURL, {
                nextCache: { revalidate: 60 * 60 * 24 * 7 }, // revalidate once per week
                timeout: 30000,
            });
            const batch = Array.isArray(data.posts) ? data.posts : [];
            if (batch.length === 0) {
                break; // no more posts
            }
            // Process WordPress Photon URLs in featured images
            const processedBatch = batch.map(post => ({
                ...post,
                featured_image: post.featured_image ? photonToOriginalUrl(post.featured_image) : post.featured_image
            }));
            posts.push(...processedBatch);
            if (requested && posts.length >= requested) {
                break; // collected enough
            }
            page++;
        }
        catch (error) {
            console.error("Error fetching WP posts:", error);
            return;
        }
    }
    // once we've fetched the posts we can also compare the "modified" date from
    // the first post (which is the most recent) with a fresh timestamp obtained
    // via getWordPressLastModified.  if the lastModified indicates a newer update than
    // what we just fetched, bust the cache so future callers get the latest data.
    // Only run this on the server ( no window object ) where revalidateTag is available
    if (typeof window === 'undefined' && posts && posts.length > 0 && posts[0].modified) {
        const lastModified = await getWordPressLastModified({ site: props.site, baseURL });
        if (lastModified && lastModified !== posts[0].modified) {
            // our cached response is stale relative to origin - bust Next.js cache
            import('next/cache').then(({ revalidateTag }) => {
                revalidateTag(`wp-posts-${props.site}`, {});
            });
        }
    }
    posts = posts.sort((a, b) => ((a.date ?? '') < (b.date ?? '')) ? 1 : -1);
    return posts;
}
getCachedWordPressItems.propTypes = {
    /** WordPress site identifier (slug or domain) */
    site: PropTypes.string,
    /** Number of posts to fetch (optional) */
    count: PropTypes.number,
    /** Base URL for WordPress API (optional) */
    baseURL: PropTypes.string,
};
export async function getCachedWordPressItems(props = {}) {
    const site = props.site ?? '';
    if (!site)
        return undefined;
    const baseURL = props.baseURL ?? wpApiURL;
    const key = `posts-${site}-${encodeURIComponent(baseURL)}`;
    let posts = wpCache.get(key) || undefined;
    if (!posts) {
        posts = await getWordPressItems({ site, baseURL });
        if (posts)
            wpCache.set(key, posts);
    }
    if (posts && posts.length > 0 && posts[0].modified) {
        const lastModified = await getWordPressLastModified({ site, baseURL });
        if (lastModified && lastModified !== posts[0].modified) {
            const freshPosts = await getWordPressItems({ site, baseURL: wpApiURL });
            if (freshPosts && freshPosts.length > 0) {
                wpCache.set(key, freshPosts);
                posts = freshPosts;
            }
        }
    }
    return posts?.slice(0, props.count ?? posts.length);
}
/*
 * Retrieve the modified timestamp of the most recent post on a WP site.
 * WordPress doesn’t support HEAD on the posts endpoint, so we fetch a single
 * post and pull the `modified` field from the JSON payload.  This is still
 * very light-weight compared to fetching the whole collection.
 *
 * @param props.site - WordPress site slug or domain
 * @param props.baseURL - Optional API base URL (defaults to WordPress public API)
 * @returns the ISO modified string or null if unavailable/error.
 */
export async function getWordPressLastModified(props) {
    const { baseURL = wpApiURL } = props;
    const url = buildUrl({
        baseUrl: baseURL,
        pathSegments: [props.site, 'posts'],
        params: { number: 1, fields: 'modified' },
    });
    try {
        const data = await smartFetch(url, {});
        const modified = Array.isArray(data.posts) && data.posts[0]?.modified;
        return modified || null;
    }
    catch (e) {
        console.error('Error fetching WP last-modified value', e);
        return null;
    }
}
export async function uploadWordPressMedia(input) {
    if (!input.site || !input.apiToken || !input.filename || !input.buffer.length) {
        throw new Error('WordPress media upload requires site, apiToken, filename, and image data');
    }
    const url = buildUrl({
        baseUrl: input.baseURL ?? wpApiURL,
        pathSegments: [input.site, 'media', 'new'],
    });
    const form = new FormData();
    const imageBytes = new Uint8Array(input.buffer.length);
    imageBytes.set(input.buffer);
    form.append('media[]', new Blob([imageBytes.buffer], { type: input.mimeType }), input.filename);
    form.append('attrs[0][title]', input.title);
    form.append('attrs[0][alt]', input.altText);
    form.append('attrs[0][caption]', input.caption);
    form.append('attrs[0][description]', input.description);
    const response = await smartFetch(url, {
        timeout: 60000,
        retries: 0,
        requestInit: {
            method: 'POST',
            headers: { Authorization: `Bearer ${input.apiToken}` },
            body: form,
        },
    });
    if (Array.isArray(response.errors) && response.errors.length > 0) {
        throw new Error('WordPress media upload failed');
    }
    return response;
}
/** Create a private draft through the authenticated WordPress.com API. */
export async function createWordPressDraft(input) {
    if (!input.site || !input.apiToken || !input.title || !input.content) {
        throw new Error('WordPress draft requires site, apiToken, title, and content');
    }
    const url = buildUrl({
        baseUrl: input.baseURL ?? wpApiURL,
        pathSegments: [input.site, 'posts', 'new'],
    });
    const body = {
        status: 'draft',
        title: input.title,
        content: htmlToWordPressBlocks(marked.parse(normalizeMarkdownLinks(input.content), { async: false })),
        ...(input.date ? { date: input.date } : {}),
        ...(input.excerpt ? { excerpt: input.excerpt } : {}),
        ...(input.featuredImageId !== undefined ? { featured_image: input.featuredImageId } : {}),
    };
    return smartFetch(url, {
        retries: 0,
        requestInit: {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${input.apiToken}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(body),
        },
    });
}
/** Update an existing private draft through the authenticated WordPress.com API. */
export async function updateWordPressDraft(postId, input) {
    if (postId === '' || postId === undefined || postId === null || !input.site || !input.apiToken || (!input.title && !input.content && input.date === undefined && input.featuredImageId === undefined)) {
        throw new Error('WordPress draft update requires postId, site, apiToken, and at least one update field');
    }
    const url = buildUrl({
        baseUrl: input.baseURL ?? wpApiURL,
        pathSegments: [input.site, 'posts', String(postId)],
    });
    const body = {
        status: 'draft',
        ...(input.title ? { title: input.title } : {}),
        ...(input.content ? { content: htmlToWordPressBlocks(marked.parse(normalizeMarkdownLinks(input.content), { async: false })) } : {}),
        ...(input.date ? { date: input.date } : {}),
        ...(input.excerpt ? { excerpt: input.excerpt } : {}),
        ...(input.featuredImageId !== undefined ? { featured_image: input.featuredImageId } : {}),
    };
    return smartFetch(url, {
        retries: 0,
        requestInit: {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${input.apiToken}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(body),
        },
    });
}
/**
 * getWordPressItemImages — Extract image objects from a WordPress post for use in sitemaps and galleries.
 *
 * @param {object} [props.item] - WordPress post object to extract image URLs from.
 */
getWordPressItemImages.propTypes = {
    /** WordPress post object */
    item: PropTypes.object.isRequired,
};
export function getWordPressItemImages(item) {
    const images = [];
    const seen = new Set();
    // Helper to swap image origin with post origin
    const swapOrigin = (url) => {
        try {
            const postOrigin = new URL(item.URL).origin;
            const urlObj = new URL(url);
            return `${postOrigin}${urlObj.pathname}`;
        }
        catch (error) {
            console.log("Error: ", error);
            return url;
        }
    };
    // Featured image
    if (item.featured_image && !seen.has(item.featured_image)) {
        seen.add(item.featured_image);
        images.push({
            url: swapOrigin(item.featured_image),
            title: item.title,
            caption: item.excerpt,
            thumbnail_loc: item.post_thumbnail?.URL,
        });
    }
    // Attachments
    if (item.attachments) {
        for (const key in item.attachments) {
            const att = item.attachments[key];
            if (att.URL && !seen.has(att.URL)) {
                seen.add(att.URL);
                images.push({
                    url: swapOrigin(att.URL),
                    title: att.title,
                    caption: att.caption || att.description,
                });
            }
        }
    }
    return images;
}
/**
 * getWordPressCategories — Retrieve categories for a WordPress site.
 *
 * @param {string} [props.site] - WordPress site identifier (slug or domain).
 * @param {string} [props.baseURL] - Optional base URL for the WordPress API.
 */
getWordPressCategories.propTypes = {
    /** WordPress site identifier (slug or domain) */
    site: PropTypes.string.isRequired,
    /** Base URL for WordPress API (optional) */
    baseURL: PropTypes.string,
};
export async function getWordPressCategories(props) {
    const { baseURL = wpApiURL } = props;
    const wpCategoriesURL = baseURL + props.site + wpCategoriesPath;
    const categories = [];
    try {
        const data = await smartFetch(wpCategoriesURL, {});
        const myCategories = Array.isArray(data?.categories)
            ? data.categories.map((category) => category.name)
            : [];
        categories.push(...myCategories);
    }
    catch (error) {
        console.error("Error fetching WP categories:", error);
        return;
    }
    return categories; // Return the complete list of categories
}
/**
 * Convert a WordPress Photon CDN URL to the original direct image URL
 * @param photonUrl - The Photon CDN URL (e.g., https://i0.wp.com/domain.com/path)
 * @returns The original direct image URL (e.g., https://domain.com/path)
 */
export function photonToOriginalUrl(photonUrl) {
    if (typeof photonUrl !== 'string' || !photonUrl.includes('i0.wp.com/')) {
        return photonUrl; // Return unchanged if not a Photon URL
    }
    try {
        // Photon URL format: https://i0.wp.com/domain.com/path/to/image.jpg?params
        // Extract original: https://domain.com/path/to/image.jpg
        const photonUrlObj = new URL(photonUrl);
        const pathWithoutLeadingSlash = photonUrlObj.pathname.slice(1); // Remove leading /
        const firstSlashIndex = pathWithoutLeadingSlash.indexOf('/');
        const domain = pathWithoutLeadingSlash.slice(0, firstSlashIndex);
        const path = pathWithoutLeadingSlash.slice(firstSlashIndex);
        return `https://${domain}${path}`;
    }
    catch (e) {
        console.warn('Failed to parse Photon URL:', photonUrl, e);
        return photonUrl; // Return original on error
    }
}
